import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Footer from '@/components/Footer';
import PageWrapper from '@/components/PageWrapper';
import { useLogin } from '@/hooks/api/useAuth';
import { Button, Input } from '@/components/ui';
import {
  testAuthFlow,
  testLogin,
  testLoginWithUsername,
} from '@/utils/authTest';
import { testCookieDomains, checkCookieSupport } from '@/utils/cookieTest';
import { getAuthMode } from '@/contexts/AuthWrapper';
import styles from '@/styles/pages/LoginPage.module.css';

const primary = 'var(--primary-base)';
const greyBg = 'var(--surface-surface_2)';
const greyText = 'var(--text-low_em)';
const error = 'var(--error-color)';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loginMutation = useLogin();
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [usernameOrEmailErr, setUsernameOrEmailErr] = useState('');
  const [pwdErr, setPwdErr] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const validateForm = () => {
    let valid = true;

    if (!usernameOrEmail.trim()) {
      setUsernameOrEmailErr('Username or email is required');
      valid = false;
    } else {
      setUsernameOrEmailErr('');
    }

    if (password.length < 8) {
      setPwdErr('Password must be at least 8 characters');
      valid = false;
    } else {
      setPwdErr('');
    }

    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Determine if input is email or username based on format
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(usernameOrEmail);

      // Prepare credentials - send both fields but only populate the relevant one
      const credentials = isEmail
        ? { email: usernameOrEmail, password }
        : { username: usernameOrEmail, password };

      console.log('LoginPage: Submitting login with credentials:', {
        isEmail,
        field: isEmail ? 'email' : 'username',
      });
      await loginMutation.mutateAsync(credentials);

      console.log('LoginPage: Login successful, redirecting...');
      // Redirect to the page they were trying to access (e.g. from Popular Models Chat), or text chat
      const from = location.state?.from;
      const pathname = from?.pathname || '/text-chat';
      const state = from?.state;
      navigate(pathname, { replace: true, state });
    } catch (error) {
      console.error('Login failed:', error);

      // Handle specific error messages
      let errorMessage =
        'Login failed. Please check your credentials and try again.';
      if (error instanceof Error) {
        if (
          error.message.includes('409') ||
          error.message.includes('already logged in')
        ) {
          // User is already logged in, redirect them to intended page
          console.log('User already logged in, redirecting...');
          const from = location.state?.from;
          const pathname = from?.pathname || '/text-chat';
          const state = from?.state;
          navigate(pathname, { replace: true, state });
          return; // Exit early, don't show error
        } else if (error.message.includes('401')) {
          errorMessage =
            'Invalid credentials. Please check your email/username and password.';
        } else if (error.message.includes('Network')) {
          errorMessage =
            'Network error. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout. Please try again.';
        }
      }

      setPwdErr(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--surface-surface_0)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <main
        className={styles.loginMain}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PageWrapper>
          <form
            className={styles.loginForm}
            onSubmit={handleSubmit}
            style={{
              width: 480,
              padding: '48px 32px',
              borderRadius: 24,
              background: 'var(--surface-surface_0)',
              border: '1px solid var(--outline-base_em, #F4F4F6)',
              boxShadow:
                '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), 0px 24px 24px -12px var(--elevationshadow)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 24,
            }}
          >
            <h1
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: 64,
                lineHeight: '48px',
                color: primary,
                margin: 0,
                letterSpacing: '-0.64px',
              }}
            >
              swarmind
            </h1>

            {/* Development Auth Mode Indicator */}
            {process.env.NODE_ENV === 'development' && (
              <div
                style={{
                  padding: '6px 10px',
                  backgroundColor: getAuthMode().isAuthDisabled
                    ? '#fef3c7'
                    : '#dbeafe',
                  color: getAuthMode().isAuthDisabled ? '#92400e' : '#1e40af',
                  borderRadius: '4px',
                  fontSize: '11px',
                  border: `1px solid ${getAuthMode().isAuthDisabled ? '#fbbf24' : '#3b82f6'}`,
                  textAlign: 'center',
                }}
              >
                {getAuthMode().isAuthDisabled
                  ? '🔧 MOCK AUTH MODE'
                  : '🔐 REAL AUTH MODE'}
              </div>
            )}

            {/* Username or Email field */}
            <Input
              type="text"
              label="Username or Email"
              placeholder="Enter username or email"
              value={usernameOrEmail}
              onChange={e => {
                setUsernameOrEmail(e.target.value);
                setUsernameOrEmailErr('');
              }}
              error={usernameOrEmailErr}
              onFocus={() => {
                // Focus handling can be added here if needed
              }}
              onBlur={() => {
                // Blur handling can be added here if needed
              }}
            />

            {/* Password field */}
            <div style={{ width: '100%' }}>
              <Input
                type={showPwd ? 'text' : 'password'}
                label="Password"
                placeholder="Enter your password"
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setPwdErr('');
                }}
                error={pwdErr}
                onFocus={() => {}}
                onBlur={() => {}}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPwd(p => !p)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {showPwd ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={pwdErr ? error : greyText}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3-11-8 1.13-2.9 3-5.23 5.32-6.64" />
                        <path d="M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={pwdErr ? error : greyText}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M2.05 12a10.97 10.97 0 0 1 19.9 0 10.97 10.97 0 0 1-19.9 0z" />
                      </svg>
                    )}
                  </button>
                }
              />
              <div
                style={{ width: '100%', textAlign: 'right', marginTop: '8px' }}
              >
                <Button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  variant="ghost"
                  style={{
                    fontSize: 12,
                    height: 'auto',
                    padding: '4px 0',
                    color: primary,
                  }}
                >
                  Forgot password?
                </Button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 12,
                background: isLoading ? greyBg : primary,
                color: isLoading ? greyText : '#ffffff',
                fontWeight: 600,
                fontSize: 15,
                border: 'none',
                boxShadow:
                  '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), inset 0 3px 4px -3px rgba(255,255,255,0.56), inset 0 0 8px -2px rgba(255,255,255,0.48)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? 'Signing in...' : 'Continue'}
            </button>

            <Button
              type="button"
              onClick={() => navigate('/register')}
              variant="ghost"
              style={{
                fontSize: 14,
                height: 'auto',
                padding: '4px 0',
                color: primary,
              }}
            >
              Create account
            </Button>

            {/* Debug buttons for development */}
            {process.env.NODE_ENV === 'development' && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                <Button
                  type="button"
                  onClick={testAuthFlow}
                  variant="ghost"
                  size="sm"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                >
                  Test API
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (usernameOrEmail && password) {
                      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                        usernameOrEmail
                      );
                      if (isEmail) {
                        testLogin(usernameOrEmail, password);
                      } else {
                        testLoginWithUsername(usernameOrEmail, password);
                      }
                    } else {
                      alert('Please fill in credentials first');
                    }
                  }}
                  variant="ghost"
                  size="sm"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                >
                  Test Login
                </Button>
                <Button
                  type="button"
                  onClick={testCookieDomains}
                  variant="ghost"
                  size="sm"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                >
                  Cookie Test
                </Button>
                <Button
                  type="button"
                  onClick={() =>
                    console.log('Cookie support:', checkCookieSupport())
                  }
                  variant="ghost"
                  size="sm"
                  style={{ fontSize: 11, padding: '4px 8px' }}
                >
                  Cookie Support
                </Button>
              </div>
            )}
          </form>
        </PageWrapper>
      </main>

      <Footer />
    </div>
  );
};

export default LoginPage;
